import i18n from '../../../i18n';
import type { FleetRestaurant } from '../types';
import {
  isScopeKpiRule,
  matchKpiValue,
  scopeKpiValue,
  summarizeKpiRule,
  type ResolvedPeriod,
  type ScopeKpiIndex,
  type ScopeKpiRule,
} from './scopeKpi';

export type ScopeFilterCombinator = 'and' | 'or';

export type ScopeFilterOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'isEmpty';

export type ScopeFilterCategory = 'location' | 'performance' | 'settings';

export type ScopeFilterFieldId =
  | 'region'
  | 'city'
  | 'label'
  | 'brainMode'
  | 'daysInAdvance'
  | 'configuredSeats'
  | 'onlineCeiling'
  | 'maxPerTimeslot'
  | 'availabilityLocks'
  | 'dwellMinutes'
  | 'seatingsPerService'
  | 'bookingDeadlineDays'
  | 'partyMax'
  | 'groupsEnabled'
  | 'guestCancelEnabled'
  | 'remindersEnabled'
  | 'addOnSales';

export type ScopeFilterFieldKind = 'enum' | 'text' | 'number';

/** Lazily fetched attribute sets (`opsCommand/getScopeAttributes`). */
export type ScopeAttributeFamily = 'settings' | 'label';

export type ScopeFilterFieldDef = {
  id: ScopeFilterFieldId;
  label: string;
  kind: ScopeFilterFieldKind;
  category: Exclude<ScopeFilterCategory, 'performance'>;
  options?: { value: string; label: string }[];
  /** Attribute family that must be loaded before this field can be evaluated. */
  needs?: ScopeAttributeFamily;
};

/** Attribute rule (location / settings). `type` is absent on rules saved before KPI rules existed. */
export type ScopeAttrRule = {
  id: string;
  type?: 'attr';
  field: ScopeFilterFieldId;
  operator: ScopeFilterOperator;
  value: string;
};

export type ScopeFilterRule = ScopeAttrRule | ScopeKpiRule;

export type ScopeFilterState = {
  rules: ScopeFilterRule[];
  /** Length = rules.length - 1; combinator[i] joins rule[i] with rule[i + 1]. */
  combinators: ScopeFilterCombinator[];
};

export type ScopeTriState = 'all' | 'some' | 'none';
export type ScopeBrainMode = 'suggest' | 'auto' | 'table' | 'off';

export type ScopeSettingsColumns = {
  brainMode: (ScopeBrainMode | null)[];
  daysInAdvance: (number | null)[];
  configuredSeats: (number | null)[];
  onlineCeiling: (number | null)[];
  maxPerTimeslot: (number | null)[];
  availabilityLocks: (number | null)[];
  dwellMinutes: (number | null)[];
  seatingsPerService: (number | null)[];
  bookingDeadlineDays: (number | null)[];
  partyMax: (number | null)[];
  groupsEnabled: (ScopeTriState | null)[];
  guestCancelEnabled: (ScopeTriState | null)[];
  remindersEnabled: (ScopeTriState | null)[];
  addOnSales: (ScopeTriState | null)[];
  shiftCount: number[];
};

/** Columnar, locale-independent attribute payload (codes, translated client-side). */
export type ScopeAttributesPayload = {
  ids: string[];
  settings?: ScopeSettingsColumns;
  labels?: (string | null)[];
};

type SettingsFieldId = Exclude<keyof ScopeSettingsColumns, 'shiftCount'>;

export type ScopeRestaurantProfile = FleetRestaurant & {
  label?: string | null;
  settings?: { [K in SettingsFieldId]?: ScopeSettingsColumns[K][number] };
};

export const SCOPE_FILTER_CATEGORY_LABELS: Record<ScopeFilterCategory, string> = {
  get location() {
    return i18n.t('chrome:scopeFilters.categories.location');
  },
  get performance() {
    return i18n.t('chrome:scopeFilters.categories.performance');
  },
  get settings() {
    return i18n.t('chrome:scopeFilters.categories.settings');
  },
};

export const SCOPE_FILTER_CATEGORY_ORDER: ScopeFilterCategory[] = [
  'location',
  'performance',
  'settings',
];

const REGIONS = [
  'Germany',
  'Austria',
  'Switzerland',
  'France',
  'Luxembourg',
  'Denmark',
  'Other',
] as const;

function regionOptions() {
  return REGIONS.map(r => ({
    value: r,
    label: i18n.t(`chrome:scopeFilters.regions.${r}`),
  }));
}

function labelOptions() {
  return [{ value: 'walkInLed', label: i18n.t('chrome:scopeFilters.labels.walkInLed') }];
}

// Codes from opsCommand/getScopeAttributes (mirrors settings_audit's brain mode).
function brainModeOptions() {
  return (['suggest', 'auto', 'table', 'off'] as const).map(value => ({
    value,
    label: i18n.t(`chrome:scopeFilters.brain.${value}`),
  }));
}

function triStateOptions() {
  return (['all', 'some', 'none'] as const).map(value => ({
    value,
    label: i18n.t(`chrome:scopeFilters.tri.${value}`),
  }));
}

function filterField(
  id: ScopeFilterFieldId,
  kind: ScopeFilterFieldKind,
  category: ScopeFilterFieldDef['category'],
  options?: () => { value: string; label: string }[],
  needs?: ScopeAttributeFamily,
): ScopeFilterFieldDef {
  return {
    id,
    kind,
    category,
    needs,
    get label() {
      return i18n.t(`chrome:scopeFilters.fields.${id}`);
    },
    get options() {
      return options?.();
    },
  };
}

export const SCOPE_FILTER_FIELDS: ScopeFilterFieldDef[] = [
  filterField('region', 'enum', 'location', regionOptions),
  filterField('city', 'text', 'location'),
  filterField('label', 'enum', 'location', labelOptions, 'label'),
  filterField('brainMode', 'enum', 'settings', brainModeOptions, 'settings'),
  filterField('daysInAdvance', 'number', 'settings', undefined, 'settings'),
  filterField('configuredSeats', 'number', 'settings', undefined, 'settings'),
  filterField('onlineCeiling', 'number', 'settings', undefined, 'settings'),
  filterField('maxPerTimeslot', 'number', 'settings', undefined, 'settings'),
  filterField('availabilityLocks', 'number', 'settings', undefined, 'settings'),
  filterField('dwellMinutes', 'number', 'settings', undefined, 'settings'),
  filterField('seatingsPerService', 'number', 'settings', undefined, 'settings'),
  filterField('bookingDeadlineDays', 'number', 'settings', undefined, 'settings'),
  filterField('partyMax', 'number', 'settings', undefined, 'settings'),
  filterField('groupsEnabled', 'enum', 'settings', triStateOptions, 'settings'),
  filterField('guestCancelEnabled', 'enum', 'settings', triStateOptions, 'settings'),
  filterField('remindersEnabled', 'enum', 'settings', triStateOptions, 'settings'),
  filterField('addOnSales', 'enum', 'settings', triStateOptions, 'settings'),
];

export function scopeFilterField(id: ScopeFilterFieldId): ScopeFilterFieldDef | undefined {
  return SCOPE_FILTER_FIELDS.find(f => f.id === id);
}

export function defaultOperatorsForField(
  field: ScopeFilterFieldDef,
): ScopeFilterOperator[] {
  // Lazily loaded attributes can be missing (e.g. no shifts) — let users
  // target that deliberately instead of silently dropping those rows.
  const empty: ScopeFilterOperator[] = field.needs ? ['isEmpty'] : [];
  switch (field.kind) {
    case 'text':
      return ['eq', 'neq', 'contains', ...empty];
    case 'enum':
      return ['eq', 'neq', ...empty];
    case 'number':
      return ['gt', 'gte', 'lt', 'lte', 'eq', 'neq', ...empty];
  }
}

export function operatorLabel(op: ScopeFilterOperator): string {
  return i18n.t(`chrome:scopeFilters.ops.${op}`);
}

export function createEmptyFilterState(): ScopeFilterState {
  return { rules: [], combinators: [] };
}

export function defaultValueForField(def: ScopeFilterFieldDef): string {
  if (def.kind === 'enum' && def.options?.length) {
    return def.options[0].value;
  }
  return '';
}

export function createFilterRule(field: ScopeFilterFieldId = 'region'): ScopeAttrRule {
  const def = scopeFilterField(field) ?? SCOPE_FILTER_FIELDS[0];
  return {
    id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type: 'attr',
    field: def.id,
    operator: defaultOperatorsForField(def)[0],
    value: defaultValueForField(def),
  };
}

/** Attribute families the current rules need loaded. */
export function attributeFamiliesForRules(rules: ScopeFilterRule[]): ScopeAttributeFamily[] {
  const needed = new Set<ScopeAttributeFamily>();
  for (const rule of rules) {
    if (isScopeKpiRule(rule)) continue;
    const needs = scopeFilterField(rule.field)?.needs;
    if (needs) needed.add(needs);
  }
  return (['settings', 'label'] as const).filter(f => needed.has(f));
}

export function buildScopeRestaurantProfiles(
  restaurants: FleetRestaurant[],
  attributes?: ScopeAttributesPayload,
): ScopeRestaurantProfile[] {
  const position = new Map((attributes?.ids ?? []).map((id, i) => [id, i]));
  const settings = attributes?.settings;
  const labels = attributes?.labels;

  return restaurants.map(r => {
    const at = position.get(r.id);
    if (at === undefined) {
      return { ...r };
    }
    const profile: ScopeRestaurantProfile = { ...r };
    if (labels) {
      profile.label = labels[at] ?? null;
    }
    if (settings) {
      const row: NonNullable<ScopeRestaurantProfile['settings']> = {};
      for (const key of Object.keys(settings) as (keyof ScopeSettingsColumns)[]) {
        if (key === 'shiftCount') continue;
        (row as Record<string, unknown>)[key] = settings[key]?.[at] ?? null;
      }
      profile.settings = row;
    }
    return profile;
  });
}

function readFieldValue(
  row: ScopeRestaurantProfile,
  field: ScopeFilterFieldId,
): string | number | null | undefined {
  switch (field) {
    case 'region':
      return row.region;
    case 'city':
      return row.city;
    case 'label':
      return row.label;
    default:
      return row.settings?.[field];
  }
}

function matchAttrRule(row: ScopeRestaurantProfile, rule: ScopeAttrRule): boolean {
  const def = scopeFilterField(rule.field);
  if (!def) {
    return true;
  }

  const actual = readFieldValue(row, rule.field);
  const empty = actual == null || actual === '';

  if (rule.operator === 'isEmpty') {
    return empty;
  }
  // "is not X" means "not known to be X" — an unknown value can't be X.
  if (empty) {
    return rule.operator === 'neq';
  }

  if (def.kind === 'number') {
    const num = typeof actual === 'number' ? actual : Number(actual);
    const target = Number(rule.value);
    if (rule.value.trim() === '' || !Number.isFinite(num) || !Number.isFinite(target)) {
      return false;
    }
    switch (rule.operator) {
      case 'eq':
        return num === target;
      case 'neq':
        return num !== target;
      case 'gt':
        return num > target;
      case 'gte':
        return num >= target;
      case 'lt':
        return num < target;
      case 'lte':
        return num <= target;
      default:
        return false;
    }
  }

  const left = String(actual).toLowerCase();
  const right = rule.value.trim().toLowerCase();
  switch (rule.operator) {
    case 'eq':
      return left === right;
    case 'neq':
      return left !== right;
    case 'contains':
      return left.includes(right);
    default:
      return false;
  }
}

export type ScopeEvalContext = {
  /** KPI rule id → loaded period metrics. A rule without data never matches. */
  kpiByRule?: Map<string, ScopeKpiIndex>;
};

export function matchScopeRule(
  row: ScopeRestaurantProfile,
  rule: ScopeFilterRule,
  ctx: ScopeEvalContext = {},
): boolean {
  if (isScopeKpiRule(rule)) {
    const index = ctx.kpiByRule?.get(rule.id);
    if (!index) {
      return false;
    }
    return matchKpiValue(scopeKpiValue(index, row.id, rule).value, rule);
  }
  return matchAttrRule(row, rule);
}

/** Left fold, no precedence: "A OR B AND C" = "(A OR B) AND C". */
export function evaluateScopeFilters(
  row: ScopeRestaurantProfile,
  state: ScopeFilterState,
  ctx: ScopeEvalContext = {},
): boolean {
  if (!state.rules.length) {
    return true;
  }
  let result = matchScopeRule(row, state.rules[0], ctx);
  for (let i = 1; i < state.rules.length; i++) {
    const comb = state.combinators[i - 1] ?? 'and';
    // Short-circuit keeps KPI derivation off rows already decided.
    if (comb === 'and' && !result) continue;
    if (comb === 'or' && result) continue;
    result = matchScopeRule(row, state.rules[i], ctx);
  }
  return result;
}

export function summarizeFilterRule(
  rule: ScopeFilterRule,
  resolved?: ResolvedPeriod,
): string {
  if (isScopeKpiRule(rule)) {
    return summarizeKpiRule(rule, resolved);
  }
  const def = scopeFilterField(rule.field);
  if (!def) {
    return '';
  }
  const op = operatorLabel(rule.operator);
  if (rule.operator === 'isEmpty') {
    return `${def.label} ${op}`;
  }
  const valueLabel =
    def.options?.find(o => o.value === rule.value)?.label ?? rule.value;
  return `${def.label} ${op} ${valueLabel}`;
}

/** "Region is Germany AND Reservations > 100 · Last 14 days". */
export function summarizeFilterState(
  state: ScopeFilterState,
  resolvedByRule?: Map<string, ResolvedPeriod>,
): string {
  return state.rules
    .map((rule, i) => {
      const text = summarizeFilterRule(rule, resolvedByRule?.get(rule.id));
      if (i === 0) return text;
      const comb = state.combinators[i - 1] ?? 'and';
      return `${i18n.t(`chrome:scopeFilters.ui.${comb}`)} ${text}`;
    })
    .join(' ');
}

/** Human-readable settings value for the restaurant subtitle. */
export function settingsValueLabel(
  field: ScopeFilterFieldId,
  value: string | number | null | undefined,
): string {
  if (value == null || value === '') {
    return i18n.t('chrome:scopeFilters.kpi.noData');
  }
  const def = scopeFilterField(field);
  return def?.options?.find(o => o.value === value)?.label ?? String(value);
}
